// Castintech dashboard — Topbar UI modulu.
// Faz 9b PR8 — agent strip + live indicator + clock + countdown.
// Faz 13 — strict TS hardening.

import { state } from '../state.js';
import { statusCls, statusLabel, ago, getGem, $, asEl } from '../utils.js';
import { ROLE_LABELS, REFRESH_INTERVAL } from '../config.js';
import { agentActiveTasks, renderEkip } from '../views/ekip.js';
import { renderKanban } from '../views/pano.js';
import { refresh } from '../main.js';

// Türkçe görünen-ad + karakter yüzü katmanı (Ekip/drawer ile aynı — /characters/).
let _dnStrip: Record<string, { person: string; code: string; role: string }> = {};
fetch('/characters/display-names.json', { cache: 'no-store' }).then(r => r.json()).then(j => { _dnStrip = j || {}; renderStrip(); }).catch(() => {});
function _aslug(n: string): string { return (n || '').toLowerCase().trim().replace(/\s+/g, '-'); }

// ── Agent strip (ust filtre seridi) ─────────────────────────────────

export function renderStrip(): void {
  const strip = $('agent-strip');
  if (!strip) return;
  if (!state.agents.length) {
    strip.innerHTML = '<span class="chip-filter-label" style="color:var(--muted)">Ajan yok</span>';
    return;
  }

  const allBtn = `<span class="chip-filter-label" title="Ekip ajanları — tıkla, panoyu o ajana göre filtrele">👥 Ekip · filtre</span>
    <div class="agent-chip ${state.agentFilter === null ? 'active-filter' : ''}" data-action="filter-agent">
      <span style="font-size:12px;color:var(--text2)">Tümü</span>
    </div>`;

  const dotColors: Record<string, string> = { active: '#22c55e', busy: '#f59e0b', error: '#ef4444', idle: '#334155' };

  const chips = state.agents.map(a => {
    const gem = getGem(a.name);
    const sc = statusCls(a.status);
    const at = a.id ? agentActiveTasks(a.id) : [];
    const cur = at[0];
    const dotColor = dotColors[sc] || '#334155';
    const pulseClass = sc === 'active' ? 'pulse-active' : sc === 'busy' ? 'pulse-busy' : '';
    const role = a.role || '';
    const roleLabel = (ROLE_LABELS as Record<string, string>)[role] || role || a.company_name || '';
    // Karakter yüzü (display-names'te varsa) — slug ajan adı, yoksa gem adı.
    const slug0 = _aslug(a.name); const slug = _dnStrip[slug0] ? slug0 : _aslug(gem.name); const dn = _dnStrip[slug];
    const nameLabel = dn ? dn.person : gem.name;
    const subLabel = dn ? `${dn.code} · ${roleLabel || dn.role}` : roleLabel;
    return `
      <div class="agent-chip ${pulseClass} ${state.agentFilter === a.id ? 'active-filter' : ''} ${cur ? 'has-task' : ''}"
           data-action="filter-agent" data-agent-id="${a.id}">
        <div class="chip-avatar ${gem.cls}">
          <div class="chip-avatar-bg"></div>
          ${dn ? `<img class="chip-face" src="/characters/face/${slug}.png?v=3" loading="lazy" onerror="this.style.display='none';this.parentElement.querySelector('.chip-letter').style.display='flex'">` : ''}
          <div class="chip-letter"${dn ? ' style="display:none"' : ''}>${gem.name[0]}</div>
          ${gem.icon ? `<div class="chip-badge">${gem.icon}</div>` : ''}
          <div class="chip-avatar-shine"></div>
          <span class="chip-status" style="background:${dotColor};box-shadow:0 0 ${sc === 'active' ? '6' : '0'}px ${dotColor};"></span>
        </div>
        <div class="chip-info">
          <div class="chip-name">${nameLabel}</div>
          <div class="chip-role">${subLabel}</div>
          ${cur ? `<div class="chip-task ${gem.cls}">${(cur.title || cur.identifier || '').substring(0, 16)}</div>` : ''}
        </div>
      </div>`;
  }).join('<div class="chip-separator"></div>');

  strip.innerHTML = allBtn + chips;
}

export function setAgentFilter(id: string | null): void {
  state.agentFilter = id;
  renderStrip();
  if (state.currentPage === 'pano') renderKanban();
  else if (state.currentPage === 'ekip') renderEkip();
}

// ── Live indicator (aktif ajan + ticker) ────────────────────────────

export function renderLiveIndicator(): void {
  const active = state.agents.filter(a => ['active', 'running', 'busy'].includes(statusCls(a.status)));
  const label = $('live-label');
  const dot = $('live-dot');
  if (label && dot) {
    if (active.length > 0) {
      label.textContent = active.length + ' ajan aktif';
      label.style.color = 'var(--green)';
      dot.style.background = 'var(--green)';
      dot.style.boxShadow = '0 0 8px var(--green)';
    } else {
      label.textContent = 'Sistem bekliyor';
      label.style.color = 'var(--text2)';
      dot.style.background = 'var(--muted)';
      dot.style.boxShadow = 'none';
    }
  }

  // Ticker (son 8 task scroll)
  const ticker = $('ticker');
  const tickerWrap = $('ticker-wrap');
  if (!ticker || !tickerWrap) return;
  const recentTasks = state.tasks.slice(0, 8);
  if (recentTasks.length) {
    tickerWrap.style.display = '';
    const items = [...recentTasks, ...recentTasks].map(t => {
      const a = state.agents.find(x => x.id === (t.assigneeAgentId || t.assignee_id));
      const gem = a ? getGem(a.name) : null;
      return `<span class="ticker-item">
        ${gem ? `<span class="ti-gem ${gem.cls}" style="color:var(--gc)">${gem.name[0]}</span>` : ''}
        <span>${t.identifier || ''} ${t.title ? '· ' + t.title.substring(0, 30) : ''}</span>
        <span style="color:var(--border2)">·</span>
        <span>${statusLabel(t.status)}</span>
        <span style="color:var(--muted)">${ago(t.updatedAt || t.created_at)}</span>
      </span>`;
    }).join('');
    ticker.innerHTML = items;
  } else {
    tickerWrap.style.display = 'none';
  }
}

// ── Clock + Countdown timer'lari ────────────────────────────────────

export function updateClock(): void {
  const el = $('topbar-clock');
  if (el) {
    el.textContent = new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }
}

export function startCountdown(): void {
  state.countdownVal = REFRESH_INTERVAL / 1000;
  if (state.refreshTimer) clearInterval(state.refreshTimer);
  state.refreshTimer = setInterval(() => {
    state.countdownVal--;
    const el = $('refresh-countdown');
    if (el) el.textContent = state.countdownVal + 's';
    if (state.countdownVal <= 0) refresh();
  }, 1000);
}

// Modul yuklendiginde clock'u baslat (eski inline koddaki setInterval+updateClock).
setInterval(updateClock, 1000);
updateClock();

// ── Faz 10 PR1 — topbar refresh button statik click bind ────────────
let _refreshBound = false;
export function bindTopbarRefresh(): void {
  if (_refreshBound) return;
  // Topbar refresh: title="Yenile (R)" attribute ile bul (sidebar refresh ile karismasin)
  const btn = document.querySelector<HTMLElement>('.topbar .btn-icon[title^="Yenile"]');
  if (btn) btn.addEventListener('click', () => refresh());
  // 2026-07-26 — Çıkış butonu: staff + tenant cookie'lerini sunucuda temizler,
  // sonra login'e döner (tenant-cookie ezme vakasında UI'dan kurtuluş yolu).
  const logoutBtn = document.getElementById('btn-logout');
  if (logoutBtn) {
    logoutBtn.addEventListener('click', () => {
      if (!window.confirm('Oturum kapatılsın mı?')) return;
      fetch('/castintech-api/auth/logout', { method: 'POST', credentials: 'same-origin' })
        .catch(() => { /* sunucuya ulaşılamasa da login'e dön */ })
        .finally(() => window.location.replace('/login.html'));
    });
  }
  _refreshBound = true;
}

// Faz 10 PR5 — Agent strip delegated click. data-action=filter-agent + data-agent-id (yoksa null).
let _stripDelegated = false;
export function bindAgentStripDelegation(): void {
  if (_stripDelegated) return;
  const strip = $('agent-strip');
  if (!strip) return;
  strip.addEventListener('click', (e: MouseEvent) => {
    const chip = asEl(e.target)?.closest<HTMLElement>('[data-action="filter-agent"]');
    if (!chip || !strip.contains(chip)) return;
    // data-agent-id yoksa "Tumu" chip'i → setAgentFilter(null)
    const id = chip.dataset.agentId || null;
    setAgentFilter(id);
  });
  _stripDelegated = true;
}
